import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SYSTEM_NAME_ALIAS } from 'src/components/star-grid/memberSubsetConstants';
import {
  getAliasLabels,
  getHierarchyTree,
  getHierarchyTreeByPair,
  getSubsetView,
  listHierarchyAliases,
  listHierarchyViews
} from 'src/components/star-grid/services/starGridService';
import type {
  PnlHierarchyMember,
  PnlHierarchyOption,
  PnlHierarchyTree,
  PnlSubsetViewEntry,
  PnlSubsetViewSummary
} from 'src/components/star-grid/starGridTypes';
import { relabelToIds, relabelTree } from 'src/components/star-grid/utils/subset-editor/memberAliases';
import { aliasPairOf, defaultAliasOf, defaultViewIdOf, pickHierarchyOption } from 'src/components/star-grid/utils/subset-editor/memberSubsetEditor';

/**
 * Everything the editor loads from the server: the hierarchy tree, its aliases and its saved views.
 *
 * Each fetch uses a per-run cancellation flag rather than a "fetched once" latch, because StrictMode's
 * double run would otherwise leave the editor loading forever. Only the tree reports a failure; alias and
 * view failures fall back silently so they never block browsing.
 */
export const useSubsetEditorData = (
  visible: boolean,
  hierarchyId: string,
  hierarchyTree: PnlHierarchyTree | undefined,
  hierarchies: readonly PnlHierarchyOption[] | undefined,
  openedWithSelection: boolean
) => {
  const [members, setMembers] = useState<PnlHierarchyMember[]>(hierarchyTree?.members ?? []);
  const [memberCount, setMemberCount] = useState(hierarchyTree?.member_count ?? 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `null` = the hierarchy the editor was opened with; a dropdown switch stays local until OK.
  const [pickedValue, setPickedValue] = useState<string | null>(null);

  // `null` = the registered display name the rows already carry; Reset View returns here.
  const [alias, setAlias] = useState<string | null>(null);
  const [aliasNames, setAliasNames] = useState<readonly string[]>([]);
  const [aliasLabels, setAliasLabels] = useState<Readonly<Record<string, string>> | null>(null);
  const [aliasNamesLoading, setAliasNamesLoading] = useState(false);
  const [aliasLabelsLoading, setAliasLabelsLoading] = useState(false);

  const [views, setViews] = useState<readonly PnlSubsetViewSummary[]>([]);
  // `null` = the hierarchy itself, which the dropdown shows as `---`.
  const [viewId, setViewId] = useState<number | null>(null);
  const [viewRows, setViewRows] = useState<readonly PnlSubsetViewEntry[] | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewsLoaded, setViewsLoaded] = useState(false);
  // A ref, so the default view applies once per hierarchy and choosing `---` afterwards sticks.
  const appliedDefaultFor = useRef<string | null>(null);

  const picked = useMemo(() => pickHierarchyOption(hierarchies, pickedValue, hierarchyId), [hierarchies, pickedValue, hierarchyId]);
  const aliasPair = useMemo(
    () => aliasPairOf(picked?.dimension, picked?.cdp_hierarchy, hierarchyId),
    [picked?.dimension, picked?.cdp_hierarchy, hierarchyId]
  );

  useEffect(() => {
    if (!visible) return undefined;
    if (hierarchyTree) {
      setMembers(hierarchyTree.members);
      setMemberCount(hierarchyTree.member_count);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    // By pair whenever the option carries one; the by-id route only reaches registry-wired hierarchies.
    const request =
      picked?.dimension !== undefined && picked.cdp_hierarchy !== undefined
        ? getHierarchyTreeByPair(picked.dimension, picked.cdp_hierarchy, controller.signal)
        : getHierarchyTree(hierarchyId, controller.signal);
    request
      .then((tree) => {
        if (cancelled) return;
        setMembers(tree.members);
        setMemberCount(tree.member_count);
        setLoading(false);
      })
      .catch((cause: Error) => {
        if (cancelled) return;
        setError(cause.message || 'Could not load the hierarchy.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [visible, hierarchyId, hierarchyTree, picked?.dimension, picked?.cdp_hierarchy]);

  useEffect(() => {
    if (!visible || !aliasPair) return undefined;
    const { dimension, hierarchy } = aliasPair;
    let cancelled = false;
    const controller = new AbortController();
    setAliasNamesLoading(true);
    listHierarchyAliases(dimension, hierarchy, controller.signal)
      .then((response) => {
        if (cancelled) return;
        setAliasNames(response.aliases);
        setAliasNamesLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setAliasNames([]);
        setAliasNamesLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [visible, aliasPair]);

  const registeredAttribute = picked?.attribute;
  const defaultAlias = useMemo(() => defaultAliasOf(registeredAttribute, aliasNames), [registeredAttribute, aliasNames]);
  const effectiveAlias = alias ?? defaultAlias;

  // No fetch for System Name or the registered attribute: those labels are already on the nodes.
  useEffect(() => {
    const dimension = aliasPair?.dimension;
    const hierarchy = aliasPair?.hierarchy;
    if (
      !visible ||
      effectiveAlias === null ||
      effectiveAlias === SYSTEM_NAME_ALIAS ||
      effectiveAlias === registeredAttribute ||
      dimension === undefined ||
      hierarchy === undefined
    ) {
      setAliasLabels(null);
      setAliasLabelsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setAliasLabelsLoading(true);
    getAliasLabels(dimension, hierarchy, effectiveAlias, controller.signal)
      .then((response) => {
        if (cancelled) return;
        setAliasLabels(response.labels);
        setAliasLabelsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setAliasLabels(null);
        setAliasLabelsLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [visible, effectiveAlias, registeredAttribute, aliasPair]);

  // Relabelled before indexing, so search, Copy and the context menu all agree with what is on screen.
  const labelled = useMemo(() => {
    if (effectiveAlias === SYSTEM_NAME_ALIAS) return relabelToIds(members);
    return aliasLabels === null ? members : relabelTree(members, aliasLabels);
  }, [members, aliasLabels, effectiveAlias]);

  useEffect(() => {
    if (!visible || aliasPair === undefined) return undefined;
    const { dimension, hierarchy } = aliasPair;
    let cancelled = false;
    const controller = new AbortController();
    setViewsLoaded(false);
    listHierarchyViews(dimension, hierarchy, controller.signal)
      .then((response) => {
        if (cancelled) return;
        setViews(response.views);
        setViewsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setViews([]);
        setViewsLoaded(true);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [visible, aliasPair]);

  const defaultViewId = useMemo(() => defaultViewIdOf(views), [views]);
  // A restored selection wins over the default view, so the field opens on its own members.
  const openingViewId = openedWithSelection ? null : defaultViewId;

  useEffect(() => {
    if (!visible || !viewsLoaded || aliasPair === undefined) return;
    const pair = `${aliasPair.dimension}::${aliasPair.hierarchy}`;
    if (appliedDefaultFor.current === pair) return;
    appliedDefaultFor.current = pair;
    if (defaultViewId !== null && !openedWithSelection) setViewId(defaultViewId);
  }, [visible, viewsLoaded, defaultViewId, aliasPair, openedWithSelection]);

  // Rows are fetched on selection: a saved view can be thousands of rows, so the list carries none.
  useEffect(() => {
    if (!visible || viewId === null) {
      setViewRows(null);
      setViewLoading(false);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setViewLoading(true);
    getSubsetView(viewId, controller.signal)
      .then((view) => {
        if (cancelled) return;
        setViewRows(view.entries);
        setViewLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setViewRows(null);
        setViewLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [visible, viewId]);

  // Drops the members too, so the spinner shows instead of the previous hierarchy's rows.
  const switchHierarchy = useCallback((value: string) => {
    setPickedValue(value);
    setMembers([]);
    setMemberCount(0);
    // Saved views belong to one hierarchy; the new one's list (and its default) loads once the pair changes.
    setViews([]);
    setViewId(null);
  }, []);

  return {
    members,
    memberCount,
    loading,
    error,
    pickedValue,
    picked,
    aliasPair,
    switchHierarchy,
    alias,
    setAlias,
    aliasNames,
    aliasNamesLoading,
    aliasLabelsLoading,
    effectiveAlias,
    labelled,
    views,
    setViews,
    viewId,
    setViewId,
    viewRows,
    viewLoading,
    openingViewId
  };
};
